import React, { useEffect, useMemo, useState } from 'react'
import Modal from 'components/atoms/Modal'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectFileTagsForBp } from 'ducks/files/selectors'
import Button from 'components/atoms/Button'
import PrimaryButton from 'components/atoms/PrimaryButton'
import { isNotNilOrEmpty } from 'utils/ramda'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import SearchIcon from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const AttachTagsToUploadingFile = ({
  onSave,
  savedCount,
  initiallySelected,
  children
}) => {
  const VISIBLE_SELECTED_TAGS_COUNT = 5
  const tags = useSelector(selectFileTagsForBp)
  const [open, setOpen] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)
  const [showAllSelected, setShowAllSelected] = useState(false)
  const [selectedSectionExpanded, setSelectedSectionExpanded] = useState(false)

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setSearchValue('')
    setShowSelectedOnly(false)
    setShowAllSelected(false)
    setSelectedSectionExpanded(false)
    setOpen(false)
  }

  const toggleTagSelection = id => () => {
    const normalizedId = String(id)
    setSelectedTags(prev => {
      if (prev.includes(normalizedId)) {
        return prev.filter(el => el !== normalizedId)
      }
      return [...prev, normalizedId]
    })
  }

  useEffect(() => {
    setSelectedTags((initiallySelected || []).map(String))
  }, [initiallySelected])

  const selectedTagObjects = useMemo(() => {
    const selectedSet = new Set(selectedTags)
    return (tags || []).filter(tag => selectedSet.has(String(tag.id)))
  }, [selectedTags, tags])

  const filteredTags = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase()

    return [...(tags || [])]
      .filter(tag => {
        const isSelected = selectedTags.includes(String(tag.id))
        if (showSelectedOnly && !isSelected) return false
        if (!normalizedQuery) return true
        return tag.name.toLowerCase().includes(normalizedQuery)
      })
      .sort((a, b) => {
        const aSelected = selectedTags.includes(String(a.id))
        const bSelected = selectedTags.includes(String(b.id))

        if (aSelected !== bSelected) return aSelected ? -1 : 1
        return a.name.localeCompare(b.name)
      })
  }, [tags, searchValue, selectedTags, showSelectedOnly])

  const visibleSelectedTagObjects = useMemo(() => {
    if (showAllSelected) return selectedTagObjects
    return selectedTagObjects.slice(0, VISIBLE_SELECTED_TAGS_COUNT)
  }, [selectedTagObjects, showAllSelected, VISIBLE_SELECTED_TAGS_COUNT])

  const handleSubmit = () => {
    onSave(selectedTags)
    handleClose()
  }

  const handleClearAll = () => setSelectedTags([])
  const handleSelectAll = () => {
    setSelectedTags((tags || []).map(tag => String(tag.id)))
  }

  return (
    <>
      <TriggerWrapper onClick={handleOpen}>
        {children || (
          <Wrapper>
            <StyledIcon />
            {savedCount > 0 && <Badge>{savedCount}</Badge>}
          </Wrapper>
        )}
      </TriggerWrapper>
      <Modal open={open} onClose={handleClose} title='Attach tags to file'>
        <ModalContent>
          <HeaderBlock>
            <HeaderText>
              Select the tags that should be attached to this upload.
            </HeaderText>
            <MetaRow>
              <MetaBadge>{tags?.length || 0} available</MetaBadge>
              <MetaBadge $active>{selectedTags.length} selected</MetaBadge>
            </MetaRow>
          </HeaderBlock>

          <InputDescription margin='0 0 18px'>
            You cannot create new tags here. Create them in Documents first,
            then return to this uploader.
          </InputDescription>

          <Toolbar>
            <SearchField>
              <SearchFieldIcon />
              <SearchInput
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder='Search tags by name...'
              />
            </SearchField>
          </Toolbar>

          <ToolbarActions>
            <ToolbarActionGroup>
              <ClearTextButton
                type='button'
                onClick={handleSelectAll}
                disabled={
                  !isNotNilOrEmpty(tags) ||
                  selectedTags.length === (tags?.length || 0)
                }
              >
                Select all
              </ClearTextButton>
              <ClearTextButton
                type='button'
                onClick={handleClearAll}
                disabled={selectedTags.length === 0}
              >
                Clear all
              </ClearTextButton>
            </ToolbarActionGroup>

            <FilterToggle
              type='button'
              $active={showSelectedOnly}
              onClick={() => setShowSelectedOnly(prev => !prev)}
            >
              {showSelectedOnly ? 'Showing selected' : 'Show selected only'}
            </FilterToggle>
          </ToolbarActions>

          {selectedTagObjects.length > 0 && (
            <SelectedSection>
              <SelectedSectionHeader
                onClick={() => setSelectedSectionExpanded(prev => !prev)}
              >
                <SelectedSectionTitleGroup>
                  <SectionTitle>Selected tags</SectionTitle>
                  <InlineCount>{selectedTagObjects.length}</InlineCount>
                </SelectedSectionTitleGroup>
                <SelectedHeaderActions>
                  <ShowMoreButton
                    type='button'
                    onClick={e => {
                      e.stopPropagation()
                      setSelectedSectionExpanded(prev => !prev)
                    }}
                  >
                    {selectedSectionExpanded
                      ? 'Hide selected tags'
                      : 'Show selected tags'}
                  </ShowMoreButton>
                  <SelectedSectionToggle $expanded={selectedSectionExpanded} />
                </SelectedHeaderActions>
              </SelectedSectionHeader>
              {selectedSectionExpanded && (
                <SelectedChips>
                  {visibleSelectedTagObjects.map(tag => (
                    <SelectedChip key={tag.id}>
                      <span>{tag.name}</span>
                      <ChipCloseButton
                        type='button'
                        onClick={toggleTagSelection(tag.id)}
                      >
                        <ChipCloseIcon />
                      </ChipCloseButton>
                    </SelectedChip>
                  ))}
                </SelectedChips>
              )}
            </SelectedSection>
          )}

          <ListSection>
            <TagsList>
              {isNotNilOrEmpty(filteredTags) ? (
                filteredTags.map(tag => {
                  const isSelected = selectedTags.includes(String(tag.id))

                  return (
                    <TagRow
                      key={tag.id}
                      $active={isSelected}
                      onClick={toggleTagSelection(tag.id)}
                    >
                      <TagCopy>
                        <TagName>{tag.name}</TagName>
                      </TagCopy>

                      <SelectionIndicator $active={isSelected}>
                        {isSelected && <SelectionCheck />}
                      </SelectionIndicator>
                    </TagRow>
                  )
                })
              ) : (
                <EmptyState>
                  <EmptyTitle>No tags found</EmptyTitle>
                  <EmptyText>
                    Try a different search term or disable the selected-only
                    filter.
                  </EmptyText>
                </EmptyState>
              )}
            </TagsList>
          </ListSection>

          <ButtonsWrapper>
            <Button variant='outlined' onClick={handleClose}>
              Cancel
            </Button>
            <PrimaryButton onClick={handleSubmit}>
              Save{' '}
              {selectedTags.length > 0
                ? `${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'}`
                : ''}
            </PrimaryButton>
          </ButtonsWrapper>
        </ModalContent>
      </Modal>
    </>
  )
}

export default AttachTagsToUploadingFile

const TriggerWrapper = styled.div`
  width: fit-content;
  height: fit-content;
`

const ModalContent = styled.div`
  width: min(720px, 100%);
`

const HeaderBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const HeaderText = styled.div`
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti};
`

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const MetaBadge = styled.div`
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.grey[800]};
  background: ${({ $active }) =>
    $active ? 'rgba(51, 190, 204, 0.14)' : '#f2f4f7'};
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(51, 190, 204, 0.28)' : '#e4e7ec')};
`

const Toolbar = styled.div`
  margin-bottom: 12px;
`

const SearchField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 48px;
  border-radius: 14px;
  border: 1px solid #d0d5dd;
  background: #fcfcfd;
`

const SearchFieldIcon = styled(SearchIcon)`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 20px !important;
`

const SearchInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 8px;
  }
`

const ToolbarActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const FilterToggle = styled.button`
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(51, 190, 204, 0.35)' : '#d0d5dd')};
  background: ${({ $active }) =>
    $active ? 'rgba(51, 190, 204, 0.12)' : '#fff'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.grey[800]};
  height: 40px;
  border-radius: 999px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`

const ClearTextButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.grey[700] : theme.colors.primary};
  font-size: 13px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`

const SelectedSection = styled.div`
  margin-bottom: 18px;
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fdff 0%, #f6f9fb 100%);
  border: 1px solid #d9eef2;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionCount = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const SelectedHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SelectedSectionHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
`

const SelectedSectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const InlineCount = styled.div`
  min-width: 22px;
  height: 22px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #cbe8ee;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 800;
`

const ShowMoreButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`

const SelectedSectionToggle = styled(KeyboardArrowDownIcon)`
  font-size: 18px !important;
  color: ${({ theme }) => theme.colors.grey[800]};
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const SelectedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 8px 10px 8px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #cbe8ee;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 13px;
  font-weight: 600;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ChipCloseButton = styled.button`
  width: 20px;
  height: 20px;
  min-width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #eef6f8;
  cursor: pointer;
`

const ChipCloseIcon = styled(CloseIcon)`
  font-size: 14px !important;
`

const ListSection = styled.div`
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const TagsList = styled.div`
  max-height: 360px;
  overflow-y: auto;
`

const TagRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: none;
  border-top: 1px solid #f0f2f5;
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(90deg, rgba(51, 190, 204, 0.1) 0%, rgba(51, 190, 204, 0.03) 100%)'
      : '#fff'};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: ${({ $active }) =>
      $active
        ? 'linear-gradient(90deg, rgba(51, 190, 204, 0.12) 0%, rgba(51, 190, 204, 0.05) 100%)'
        : '#f8fafc'};
  }
`

const TagCopy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TagName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};
`

const SelectionIndicator = styled.div`
  width: 22px;
  height: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid ${({ $active }) => ($active ? '#33becc' : '#c7d0d9')};
  background: ${({ $active }) => ($active ? '#33becc' : '#fff')};
  transition: all 0.2s ease;
`

const SelectionCheck = styled(CheckIcon)`
  font-size: 14px !important;
  color: #fff;
`

const EmptyState = styled.div`
  padding: 36px 20px;
  text-align: center;
`

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const EmptyText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const ButtonsWrapper = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  padding-top: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 35%);

  @media (max-width: 640px) {
    flex-direction: column-reverse;
  }

  & > button {
    min-width: 140px;

    @media (max-width: 640px) {
      width: 100%;
    }
  }
`

const Wrapper = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
`

const StyledIcon = styled(LocalOfferOutlinedIcon)`
  cursor: pointer !important;
`

const Badge = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  top: -3px;
  right: -3px;
  border-radius: 50%;
`
